/**
 * UPT-2079 L14a (web §2.1 n° 5, §5.1, §6) — RÉ-ENCODAGE des images par le navigateur avant dépôt : toute image admise (PNG, JPEG, WebP, GIF, d'un `data:` du
 * prototype ou d'un fichier choisi) devient un PNG si elle a de la transparence, sinon un JPEG de qualité 0,85, côté long ≤ 1 920, ≤ 480 Kio (marge sous les
 * 512 Kio du serveur, qui ré-encode à son tour). Le décodage et le dessin se font hors document (canvas non attaché), SANS URL d'objet ; le SVG est refusé AVANT
 * tout décodage ; aucune URL distante n'est jamais récupérée. L'environnement (décodeur, canvas) est injectable : les tests le simulent.
 */
import { MODULE_ASSET_LIMITS } from '../data/contracts';

export type AssetEncodingErrorCode = 'asset.type' | 'asset.dimensions' | 'asset.too_large' | 'asset.decode';

export class AssetEncodingError extends Error {
  readonly code: AssetEncodingErrorCode;

  constructor(code: AssetEncodingErrorCode) {
    super(code);
    this.name = 'AssetEncodingError';
    this.code = code;
  }
}

export interface CanvasContextLike {
  drawImage: (image: unknown, dx: number, dy: number, dw: number, dh: number) => void;
  getImageData: (x: number, y: number, w: number, h: number) => { data: ArrayLike<number> };
  fillStyle: string;
  fillRect: (x: number, y: number, w: number, h: number) => void;
}

export interface CanvasLike {
  width: number;
  height: number;
  getContext: (kind: '2d') => CanvasContextLike | null;
  toBlob: (callback: (blob: Blob | null) => void, type?: string, quality?: number) => void;
}

export interface DecodedImage {
  width: number;
  height: number;
  /** Ce que reçoit `drawImage`. */
  source: unknown;
  close?: () => void;
}

export interface EncodeEnv {
  decode: (blob: Blob) => Promise<DecodedImage>;
  createCanvas: (width: number, height: number) => CanvasLike;
}

/** Environnement du navigateur : `createImageBitmap` et un canvas HORS document. */
export const browserEncodeEnv: EncodeEnv = {
  decode: async (blob) => {
    const bitmap = await createImageBitmap(blob);
    return { width: bitmap.width, height: bitmap.height, source: bitmap, close: () => bitmap.close() };
  },
  createCanvas: (width, height) => {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    return canvas as unknown as CanvasLike;
  },
};

export interface EncodedAsset {
  blob: Blob;
  mediaType: 'image/png' | 'image/jpeg';
  width: number;
  height: number;
  /** Octets du résultat (= `blob.size`). */
  bytes: number;
}

type AdmittedType = 'image/png' | 'image/jpeg' | 'image/webp' | 'image/gif';

/** Vrai si les premiers octets (ou le nom, ou le type annoncé) désignent un SVG : refusé AVANT tout décodage. */
export const looksLikeSvg = (head: Uint8Array, declaredType?: string, fileName?: string): boolean => {
  if ((declaredType ?? '').toLowerCase().includes('svg')) return true;
  if (/\.svgz?$/i.test(fileName ?? '')) return true;
  const text = new TextDecoder('utf-8', { fatal: false }).decode(head.subarray(0, 1024)).toLowerCase();
  return text.includes('<svg') || (text.trimStart().startsWith('<?xml') && text.includes('svg'));
};

/** Type d'image reconnu par la SIGNATURE des octets (et non par l'extension ou le type annoncé) ; `null` si ce n'est ni PNG, JPEG, WebP ni GIF. */
export const sniffAdmittedType = (bytes: Uint8Array): AdmittedType | null => {
  if (bytes.length >= 8 && bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47) return 'image/png';
  if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) return 'image/jpeg';
  if (bytes.length >= 12 && bytes[0] === 0x52 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x46 && bytes[8] === 0x57 && bytes[9] === 0x45 && bytes[10] === 0x42 && bytes[11] === 0x50) {
    return 'image/webp';
  }
  if (bytes.length >= 6 && bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x38) return 'image/gif';
  return null;
};

const toBlob = (canvas: CanvasLike, type: string, quality?: number): Promise<Blob> =>
  new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new AssetEncodingError('asset.decode'))), type, quality);
  });

const hasTransparency = (context: CanvasContextLike, width: number, height: number): boolean => {
  const { data } = context.getImageData(0, 0, width, height);
  for (let index = 3; index < data.length; index += 4) {
    if (data[index] < 250) return true;
  }
  return false;
};

/** Paliers d'essais : [échelle, qualité JPEG] ; l'échelle multiplie les dimensions déjà ramenées à 1 920. */
const JPEG_STEPS: ReadonlyArray<readonly [number, number]> = [
  [1, MODULE_ASSET_LIMITS.jpegQuality],
  [1, 0.75],
  [1, 0.65],
  [1, 0.55],
  [0.9, 0.75],
  [0.8, 0.75],
  [0.7, 0.7],
  [0.6, 0.7],
  [0.5, 0.7],
  [0.4, 0.65],
  [0.3, 0.65],
];
const PNG_STEPS: readonly number[] = [1, 0.9, 0.8, 0.7, 0.6, 0.5, 0.4, 0.3, 0.2];

/**
 * Ré-encode une image admise : PNG si elle a de la transparence, sinon JPEG 0,85 ; côté long ≤ 1 920 ; si le résultat dépasse `maxBytes` (480 Kio), la qualité
 * puis l'échelle baissent par paliers fixes ; rien d'acceptable → `asset.too_large`. Un décodage impossible → `asset.decode`. Refuse un SVG (`asset.type`).
 */
export const reencodeImage = async (
  source: Blob,
  env: EncodeEnv = browserEncodeEnv,
  maxBytes: number = MODULE_ASSET_LIMITS.clientMaxBytes
): Promise<EncodedAsset> => {
  const head = new Uint8Array(await readBlobBytes(source.slice(0, 1024)));
  if (looksLikeSvg(head, source.type, (source as File).name)) throw new AssetEncodingError('asset.type');
  if (sniffAdmittedType(head) === null) throw new AssetEncodingError('asset.type');
  let decoded: DecodedImage;
  try {
    decoded = await env.decode(source);
  } catch {
    throw new AssetEncodingError('asset.decode');
  }
  try {
    if (!(decoded.width >= 1 && decoded.height >= 1)) throw new AssetEncodingError('asset.dimensions');
    const fit = Math.min(1, MODULE_ASSET_LIMITS.maxSide / Math.max(decoded.width, decoded.height));
    const baseWidth = Math.max(1, Math.round(decoded.width * fit));
    const baseHeight = Math.max(1, Math.round(decoded.height * fit));

    const probe = env.createCanvas(baseWidth, baseHeight);
    const probeContext = probe.getContext('2d');
    if (!probeContext) throw new AssetEncodingError('asset.decode');
    probeContext.drawImage(decoded.source, 0, 0, baseWidth, baseHeight);
    const transparent = hasTransparency(probeContext, baseWidth, baseHeight);

    const render = async (scale: number, quality: number | undefined): Promise<EncodedAsset> => {
      const width = Math.max(1, Math.round(baseWidth * scale));
      const height = Math.max(1, Math.round(baseHeight * scale));
      const canvas = env.createCanvas(width, height);
      const context = canvas.getContext('2d');
      if (!context) throw new AssetEncodingError('asset.decode');
      if (!transparent) {
        // JPEG : pas de transparence, fond blanc par sécurité avant le dessin.
        context.fillStyle = 'white';
        context.fillRect(0, 0, width, height);
      }
      context.drawImage(decoded.source, 0, 0, width, height);
      const mediaType = transparent ? 'image/png' : 'image/jpeg';
      const blob = await toBlob(canvas, mediaType, transparent ? undefined : quality);
      return { blob, mediaType, width, height, bytes: blob.size };
    };

    const steps: ReadonlyArray<readonly [number, number | undefined]> = transparent ? PNG_STEPS.map((scale) => [scale, undefined] as const) : JPEG_STEPS;
    for (const [scale, quality] of steps) {
      // eslint-disable-next-line no-await-in-loop -- paliers successifs, chacun dépend de la taille du précédent
      const encoded = await render(scale, quality);
      if (encoded.bytes <= maxBytes) return encoded;
    }
    throw new AssetEncodingError('asset.too_large');
  } finally {
    decoded.close?.();
  }
};

/** Octets d'un Blob (lecteur de fichier : `Blob.arrayBuffer()` n'existe pas partout). */
export const readBlobBytes = (blob: Blob): Promise<ArrayBuffer> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(new Error('read failed'));
    reader.readAsArrayBuffer(blob);
  });
